<!-- SideBarLeftNavigation.vue -->
<script setup lang="ts">
import { ref, computed } from 'vue';
import type { NavItem } from '@nuxt/content';

// Props for navigation items and drawer width
const props = defineProps({
  navigation: {
    type: Array as PropType<NavItem[]>,
    required: true,
  },
  drawerWidth: {
    type: Number,
    default: 500,
  },
  leftDrawerOpen: {
    type: Boolean,
    default: true,
  },
});

const leftOriginalWidth = ref(500);
const leftOriginal = ref(500);
const leftDrawerWidth = ref(props.drawerWidth);

// Function to handle resizing
const handlePanLeft = ({ evt, ...newInfo }) => {
  if (newInfo.isFirst) {
    leftOriginalWidth.value = leftDrawerWidth.value;
    leftOriginal.value = newInfo.position.left;
  } else {
    const newDelta = newInfo.position.left - leftOriginal.value;
    const newWidth = Math.max(200, Math.min(800, leftOriginalWidth.value + newDelta));
    leftDrawerWidth.value = newWidth;
  }
};
</script>

<template>
  <q-drawer
    class="sidebar-left full-height"
    show-if-above
    v-model="props.leftDrawerOpen"
    side="left"
    :width="leftDrawerWidth"

  >
    <div class="row fit">
      <div class="col sidebar-left sidebar-scroll">
        <nav>
          <ul>
            <NavigationItem v-for="(item, index) in props.navigation" :key="index" :item="item" />
          </ul>
        </nav>
      </div>
      <div class="sidebar-divider" v-touch-pan.horizontal.prevent.mouse.preserveCursor="handlePanLeft">
        <div class="divider-grip"></div>
      </div>
    </div>
  </q-drawer>
</template>

<style>
/* Unscoped: hide Quasar drawer's own scrollbar */
.q-drawer ::-webkit-scrollbar {
  width: 0;
  display: none;
}
.q-drawer {
  scrollbar-width: none;
}
.q-drawer__content {
  overflow-x: hidden !important;
  scrollbar-width: none;
}
.q-drawer__content::-webkit-scrollbar {
  width: 0;
  display: none;
}
</style>

<style scoped>
.full-height {
  height: 100vh;
}

.sidebar-left {
  /* Sidebar bg is chrome, not content — anchor to dark across all
     themes so the contents list stays legible even on light themes
     (e.g. NERD Lab Mint). Text colours still follow the theme. */
  background-color: #050a0e;
  color: var(--cc-link, #00ffea);
  padding: 8px;
  font-family: var(--cc-font-mono, 'Share Tech Mono', 'Courier New', monospace);
}

.sidebar-scroll {
  height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
}

.sidebar-scroll::-webkit-scrollbar {
  width: 0;
  display: none;
}

.sidebar-scroll:hover {
  scrollbar-width: thin;
  scrollbar-color: var(--cc-table-border, #00ffea30) transparent;
}

.sidebar-scroll:hover::-webkit-scrollbar {
  width: 4px;
  display: block;
}

.sidebar-scroll:hover::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-scroll:hover::-webkit-scrollbar-thumb {
  background: var(--cc-table-border, #00ffea25);
  border-radius: 2px;
}

.sidebar-scroll:hover::-webkit-scrollbar-thumb:hover {
  background: var(--cc-accent, #00ffea50);
}

.sidebar-left nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sidebar-left nav ul li {
  margin-bottom: 4px;
}

.sidebar-left nav ul li a {
  color: var(--cc-link, #00ffea);
  text-decoration: none;
}

.sidebar-left nav ul li a:hover {
  color: var(--cc-heading, #ff6600);
  text-decoration: none;
}

/* Sci-fi divider handle — anchored dark to match the sidebar chrome. */
.sidebar-divider {
  width: 6px;
  cursor: col-resize;
  background: #080e12;
  border-left: 1px solid var(--cc-table-border, #00ffea10);
  border-right: 1px solid var(--cc-table-border, #00ffea10);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.sidebar-divider:hover {
  background: #0a1418;
  border-left-color: var(--cc-table-border, #00ffea25);
  border-right-color: var(--cc-table-border, #00ffea25);
}

.divider-grip {
  width: 2px;
  height: 24px;
  background: var(--cc-table-border, #00ffea15);
  border-radius: 1px;
  transition: all 0.3s;
}

.sidebar-divider:hover .divider-grip {
  background: var(--cc-accent, #00ffea35);
  height: 36px;
}
</style>
